import { ArrowThinRightIcon, Skeleton, Typography } from "@codat/orchard-ui";
import classNames from "classnames";
import React, { CSSProperties } from "react";
import { Link } from "react-router-dom";

import styles from "./DashboardPill.module.scss";

interface DashboardPillStyles extends CSSProperties {
  "--color": string;
  "--border-color": string;
  "--background-color": string;
  "--border-color-on-hover": string;
}

export interface DashboardPillProps {
  className?: string;
  title: string;
  icon: React.ReactNode;
  count?: string;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderColorOnHover?: string;
  isSkeleton?: boolean;
  href?: string;
}

type DashboardPillWrapperProps = Omit<
  React.PropsWithChildren<DashboardPillProps>,
  "title" | "icon"
> & { style: DashboardPillStyles };

const DashboardPillWrapper: React.FC<DashboardPillWrapperProps> = ({
  href,
  children,
  isSkeleton,
  ...otherProps
}) => {
  return href && !isSkeleton ? (
    <Link to={href} {...otherProps}>
      {children}
    </Link>
  ) : (
    <div {...otherProps}>{children}</div>
  );
};

export const DashboardPill = (
  props: React.PropsWithChildren<DashboardPillProps>
) => {
  const {
    className,
    title,
    icon,
    color,
    backgroundColor,
    borderColor,
    borderColorOnHover,
    count = "0",
    isSkeleton,
    href,
    ...otherProps
  } = props;

  return (
    <DashboardPillWrapper
      href={href}
      className={classNames(styles.container, className, {
        [styles.skeleton]: isSkeleton,
      })}
      aria-busy={isSkeleton}
      data-testid="dashboard-pill"
      style={
        {
          "--color": color,
          "--border-color": borderColor,
          "--background-color": backgroundColor,
          "--border-color-on-hover": borderColorOnHover,
        } as DashboardPillStyles
      }
      isSkeleton={isSkeleton}
      {...otherProps}
    >
      <>
        {isSkeleton ? (
          <Skeleton height="100%" width="100%" className={styles.skeleton} />
        ) : (
          <div>
            <Typography as="h2" variant="body_sm" className={styles.title}>
              <span data-testid="dashboard-pill-icon" className={styles.icon}>
                {icon}
              </span>{" "}
              {title}
            </Typography>
            <Typography as="div" variant="heading_lg" className={styles.count}>
              {count}
            </Typography>
            {href && (
              <ArrowThinRightIcon
                data-testid="dashboard-pill-arrow-icon"
                className={styles.arrowIcon}
              />
            )}
          </div>
        )}
      </>
    </DashboardPillWrapper>
  );
};
